import { atom, selector, selectorFamily } from 'recoil'

import {
  isValidEmail,
  isValidMobileNumber,
  isValidMobilePrefix,
} from 'Helpers/validators'

import type {
  GroupInfo,
  PanelAddRecipientGroupsPagingState,
  PanelAddRecipientState,
} from 'Pages/RecipientsList/model'

export const panelAddRecipientState = atom<PanelAddRecipientState>({
  key: 'recipientsList_panelAddRecipientState',
  default: {
    show: false,
    recipient: {
      Email: '',
      MobilePrefix: '',
      MobileNumber: '',
      Fields: [],
      Groups: [],
    },
    isDirty: false,
    sendConfirmation: false,
  },
})

export const panelAddRecipientGroupsState =
  atom<PanelAddRecipientGroupsPagingState>({
    key: 'recipientsList_panelAddRecipientGroupsState',
    default: {
      loading: false,
      items: [],
      pageSize: 5,
      pageNumber: 1,
      orderBy: 'Id',
      orderDescending: true,
    },
  })

export const panelAddRecipientGetFieldValueState = selectorFamily<
  string,
  number
>({
  key: 'recipientsList_panelAddRecipientGetFieldValueState',
  get:
    (fieldId: number) =>
    ({ get }) => {
      const {
        recipient: { Fields },
      } = get(panelAddRecipientState)

      const field = Fields.find((f) => f.id === fieldId)

      return field?.value ?? ''
    },
})

const groupsSort =
  (orderBy: 'Id' | 'Name', orderDescending: boolean) =>
  (a: GroupInfo, b: GroupInfo): number => {
    const cmp = orderBy === 'Id' ? b.Id - a.Id : b.Name.localeCompare(a.Name)

    return orderDescending ? cmp : -1 * cmp
  }

export const panelAddRecipientPagedGroupsState = selector<GroupInfo[]>({
  key: 'recipientsList_panelAddRecipientPagedGroupsState',
  get: ({ get }) => {
    const { items, pageSize, pageNumber, orderBy, orderDescending } = get(
      panelAddRecipientGroupsState,
    )

    const sorted = [...items].sort(groupsSort(orderBy, orderDescending))

    return sorted.slice((pageNumber - 1) * pageSize, pageNumber * pageSize)
  },
})

export const panelAddRecipientIsEmailValidState = selector<boolean>({
  key: 'recipientsList_panelAddRecipientIsEmailValidState',
  get: ({ get }) => {
    const {
      recipient: { Email },
    } = get(panelAddRecipientState)

    return Email.length === 0 || isValidEmail(Email)
  },
})

export const panelAddRecipientIsMobileValidState = selector<boolean>({
  key: 'recipientsList_panelAddRecipientIsMobileValidState',
  get: ({ get }) => {
    const {
      recipient: { MobilePrefix, MobileNumber },
    } = get(panelAddRecipientState)

    const mobile = `${MobilePrefix}${MobileNumber}`

    return (
      mobile.length === 0 ||
      (isValidMobilePrefix(MobilePrefix) && isValidMobileNumber(MobileNumber))
    )
  },
})

export const panelAddRecipientIsRequiredFeedbackState = selector<boolean>({
  key: 'recipientsList_panelAddRecipientIsRequiredFeedbackState',
  get: ({ get }) => {
    const {
      isDirty,
      recipient: { Email, MobilePrefix, MobileNumber },
    } = get(panelAddRecipientState)

    return (
      isDirty &&
      Email.length === 0 &&
      MobilePrefix.length === 0 &&
      MobileNumber.length === 0
    )
  },
})

export const panelAddRecipientCanAddState = selector<boolean>({
  key: 'recipientsList_panelAddRecipientCanAddState',
  get: ({ get }) => {
    const {
      recipient: { Email, MobileNumber, MobilePrefix },
    } = get(panelAddRecipientState)

    const validEmail = get(panelAddRecipientIsEmailValidState)
    const validMobile = get(panelAddRecipientIsMobileValidState)

    return (
      (Email.length > 0 ||
        (MobilePrefix.length > 0 && MobileNumber.length > 0)) &&
      validEmail &&
      validMobile
    )
  },
})
